/**
 * The GameAudio the game talks to where there is WebAudio. The AudioContext is not made until
 * the first gesture (`unlock`), which keeps browsers from complaining and means nothing can pile
 * up in a suspended context: until then effects are dropped, and the music asked for is
 * remembered and started on the first key. Music files, if any, play through audio elements and
 * need no context at all.
 *
 * Nothing here may throw into the game loop: a browser that refuses some part of the audio graph
 * costs the sound it refused, is reported once on the console, and the game plays on.
 */

import type { SimEvent } from '../sim/types';
import { Engine } from './engine';
import { FileMusic } from './fileMusic';
import { clampVolumes, sliderGain } from './levels';
import { playsOnce } from './music/songs';
import { type Ambience, DEFAULT_VOLUMES, type GameAudio, type MusicTrack, type UiSound, type Volumes } from './types';

/** Files are finished recordings, louder than the synth: they sit a little lower. */
const FILE_TRIM = 0.7;

export class LiveAudio implements GameAudio {
  private engine: Engine | null = null;
  /** The context could not be made: stay silent rather than try on every key. */
  private failed = false;
  private warned = false;
  private readonly files = new FileMusic();
  private volumes: Volumes = { ...DEFAULT_VOLUMES };
  private wanted: MusicTrack | null = null;
  /** Whether `wanted` is being played from a file (true) or the synth (false). */
  private fromFile = false;

  constructor(private readonly Ctor: typeof AudioContext) {
    this.applyFileVolume();
  }

  unlock(): void {
    if (!this.engine && !this.failed) {
      try {
        this.engine = new Engine(this.Ctor, this.volumes);
      } catch {
        // No audio device, or the browser will not give a context: the game is silent, and that is all.
        this.failed = true;
      }
      const wanted = this.wanted;
      if (wanted && !this.fromFile) this.guard(() => this.engine?.playSong(wanted));
    }
    this.guard(() => {
      this.engine?.resume();
      this.files.unlock();
    });
  }

  events(events: readonly SimEvent[]): void {
    if (events.length === 0) return;
    // The game-over phrase is written as a line of the synthesised `gameover` track, which the game
    // starts on the same event. A recording dropped in for that track may be in any key: it plays alone.
    const heard = this.files.has('gameover') ? events.filter((e) => e.type !== 'gameOver') : events;
    this.guard(() => this.engine?.events(heard));
  }

  ambience(a: Ambience): void {
    this.guard(() => this.engine?.ambience(a));
  }

  ui(s: UiSound): void {
    this.guard(() => this.engine?.ui(s));
  }

  /**
   * Asking for what is already playing changes nothing (so it is safe to call every frame); a
   * once-only track that has finished stays finished until something else is asked for.
   */
  music(track: MusicTrack | null): void {
    if (track === this.wanted) return;
    this.wanted = track;
    this.guard(() => {
      if (track === null) {
        this.files.stop();
        this.engine?.stopSong();
        return;
      }
      const once = playsOnce(track);
      this.fromFile = this.files.play(track, once);
      if (this.fromFile) {
        this.engine?.stopSong(once);
      } else {
        this.files.stop(once);
        this.engine?.playSong(track);
      }
    });
  }

  setVolumes(v: Volumes): void {
    this.volumes = clampVolumes(v);
    this.guard(() => {
      this.engine?.setVolumes(this.volumes);
      this.applyFileVolume();
    });
  }

  hush(): void {
    this.guard(() => this.engine?.hush());
  }

  tracks(): string[] {
    return this.files.names();
  }

  private applyFileVolume(): void {
    this.files.setVolume(sliderGain(this.volumes.master) * sliderGain(this.volumes.music) * FILE_TRIM);
  }

  private guard(run: () => void): void {
    try {
      run();
    } catch (e) {
      if (this.warned) return;
      this.warned = true;
      console.warn('audio: a sound could not be played', e);
    }
  }
}
